import { useEffect } from 'react';
import { AppShell } from '@/components/AppShell';
import { useGetDashboardSummary, useGetDashboardActivity } from '@workspace/api-client-react';
import { Globe, CheckCircle2, TrendingUp, FileSearch, Activity, Clock } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { formatDistanceToNow } from 'date-fns';

// Belt-and-braces: if Clerk's post-sign-up redirect lands the user on
// /dashboard instead of / (e.g. env var override, cached redirect URL),
// detect the pending scan key and bounce back to / so SignedInHome can
// pick it up. The key name must stay in sync with PENDING_SCAN_KEY in App.tsx.
const PENDING_SCAN_KEY = 'pendingScanUrl';

export default function DashboardPage() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (localStorage.getItem(PENDING_SCAN_KEY)) {
      setLocation('/', { replace: true } as Parameters<typeof setLocation>[1]);
    }
    // Run once on mount only — the key is consumed / removed by SignedInHome.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: summary, isLoading: summaryLoading } = useGetDashboardSummary();
  const { data: activity, isLoading: activityLoading } = useGetDashboardActivity();

  const stats = [
    {
      label: 'Total Sites',
      value: summary?.totalSites ?? 0,
      icon: Globe,
      color: 'text-chart-1',
      bgColor: 'bg-chart-1/10',
    },
    {
      label: 'Verified Sites',
      value: summary?.verifiedSites ?? 0,
      icon: CheckCircle2,
      color: 'text-chart-1',
      bgColor: 'bg-chart-1/10',
    },
    {
      label: 'Monthly Visits',
      value: (summary?.totalMonthlyVisits ?? 0).toLocaleString(),
      icon: TrendingUp,
      color: 'text-chart-2',
      bgColor: 'bg-chart-2/10',
    },
    {
      label: 'Average Score',
      value: summary?.averageScore ? `${summary.averageScore}/100` : '—',
      icon: FileSearch,
      color: 'text-chart-4',
      bgColor: 'bg-chart-4/10',
    },
  ];

  const activityKindLabels: Record<string, string> = {
    scan: 'Scan completed',
    verification: 'Installation verified',
    site_created: 'Site added',
    config_updated: 'Configuration updated',
  };

  if (summary && summary.totalSites === 0) {
    return (
      <AppShell>
        <div className="p-4 py-10 md:p-8 max-w-7xl mx-auto min-h-[calc(100vh-6rem)] flex items-center justify-center">
          <div className="max-w-2xl w-full text-center space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mx-auto w-24 h-24 bg-primary/10 text-primary rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-primary/20">
              <Globe className="w-12 h-12" />
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-foreground">
                Welcome to ADA Widget
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto">
                Make your website more accessible in minutes. We will guide you through adding the widget and running your first scan. No coding experience required.
              </p>
            </div>

            <div className="pt-8">
              <Link href="/sites?add=1" className="inline-flex h-14 items-center justify-center rounded-full bg-primary px-8 text-base font-bold text-primary-foreground shadow-lg hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-200" data-testid="button-get-started-add-site">
                Add your first website
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-16 text-left">
              <div className="p-6 rounded-2xl bg-card border border-card-border shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-chart-1/10 text-chart-1 flex items-center justify-center mb-4">
                  <Activity className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">1. Add your site</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">Enter your domain name to generate a unique widget code.</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-card-border shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-chart-2/10 text-chart-2 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">2. Install the widget</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">Follow our simple guide to paste the code into your website builder.</p>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-card-border shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-chart-4/10 text-chart-4 flex items-center justify-center mb-4">
                  <FileSearch className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">3. Run a scan</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">We will check your site and highlight any accessibility improvements to make.</p>
              </div>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-sans font-bold tracking-tight text-3xl md:text-4xl text-foreground mb-2">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your accessibility monitoring</p>
        </div>

        {/* Stats Grid */}
        {summaryLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="p-6 rounded-lg border border-border bg-card animate-pulse">
                <div className="h-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="p-6 rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)] hover:shadow-md transition-shadow duration-300 stagger-item">
                  <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div className="text-3xl font-semibold text-foreground mb-1" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Activity */}
        <div className="rounded-lg border border-card-border bg-card shadow-[var(--shadow-card)]">
          <div className="p-6 border-b border-card-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
            </div>
            <Link href="/sites" className="text-sm font-medium text-primary hover:underline" data-testid="link-view-all-sites">
              View all sites
            </Link>
          </div>
          <div className="divide-y divide-card-border">
            {activityLoading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-muted" />
                    <div className="flex-1">
                      <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !activity || activity.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No activity yet</p>
                <Link href="/sites?add=1" className="text-sm text-primary hover:underline" data-testid="link-add-first-site">
                  Add your first site to get started
                </Link>
              </div>
            ) : (
              activity.slice(0, 8).map((item) => (
                <div key={item.id} className="p-4 hover:bg-muted/30 transition-colors" data-testid={`activity-${item.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Activity className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">
                            {activityKindLabels[item.kind] || item.kind}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item.siteName} — {item.description}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
